import React, { useState } from "react";
import {
  Card,
  Table,
  Input,
  Timeline,
  Empty,
  Tag,
  Button,
  Modal,
  Drawer,
} from "antd";
import {
  SearchOutlined,
  ClockCircleOutlined,
  CarOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  EyeOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchGlobalHistoryFeed_api,
  FetchVehicleTimeline_api,
} from "../../Api/Stock/stock_api";

const { Search } = Input;

export const VehicleHistory = () => {
  // Search State
  const [searchText, setSearchText] = useState("");

  // Pagination for Feed
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selected Vehicle Logic (for Detail View)
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null,
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailSearchTerm, setDetailSearchTerm] = useState(""); // Used to trigger the detail query

  // 1. Fetch Global History Feed (Main View)
  const {
    data: feedData,
    isFetching: isFetchingFeed,
    refetch: refetchFeed,
  } = useQuery({
    queryKey: ["global_history_feed", page, pageSize, searchText],
    queryFn: () => FetchGlobalHistoryFeed_api(page, pageSize, searchText),
  });

  // 2. Fetch Specific Vehicle Timeline (Detail View)
  // We only enable this when detailSearchTerm is set (via viewing details)
  const { data: detailData, isFetching: isFetchingDetail } = useQuery({
    queryKey: ["vehicle_timeline_detail", detailSearchTerm],
    queryFn: () => FetchVehicleTimeline_api(detailSearchTerm),
    enabled: !!detailSearchTerm,
  });

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleViewDetails = (record: any) => {
    // We use engine or chassis to fetch the full timeline, as the timeline API expects a search string
    setDetailSearchTerm(record.engine_number || record.chassis_number);
    setDetailModalOpen(true);
  };

  const getColor = (type: string) => {
    switch (type) {
      case "added":
        return "blue";
      case "transfer":
        return "orange";
      case "sold":
        return "green";
      default:
        return "gray";
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "added":
        return <CarOutlined />;
      case "transfer":
        return <SwapOutlined />;
      case "sold":
        return <CheckCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const feedColumns = [
    {
      title: "Date",
      dataIndex: "event_date",
      key: "event_date",
      width: 150,
      render: (text: string) => (
        <span className="text-gray-600 font-medium">{text}</span>
      ),
    },
    {
      title: "Event",
      dataIndex: "event_type",
      key: "event_type",
      width: 120,
      render: (type: string) => {
        let color = "default";
        let icon = null;
        if (type === "transfer") {
          color = "orange";
          icon = <SwapOutlined />;
        }
        if (type === "sold") {
          color = "green";
          icon = <CheckCircleOutlined />;
        }
        return (
          <Tag color={color} icon={icon}>
            {" "}
            {type.toUpperCase()}{" "}
          </Tag>
        );
      },
    },
    {
      title: "Vehicle",
      key: "vehicle",
      render: (record: any) => (
        <div>
          <div className="font-bold text-gray-800">
            {record.v_type} - {record.v_modal}
          </div>
          <div className="text-xs text-gray-500">
            ENG: {record.engine_number} | CHAS: {record.chassis_number}
          </div>
        </div>
      ),
    },
    {
      title: "Dealer / Log",
      key: "dealer_log",
      render: (record: any) => {
        if (record.event_type === "transfer") {
          return (
            <div className="flex items-center gap-2">
              <Tag>{record.from_location}</Tag>
              <ArrowRightOutlined className="text-gray-400" />
              <Tag color="blue">{record.dealer_name}</Tag>
            </div>
          );
        }
        return (
          <div>
            <span className="text-gray-500 mr-2">Sold By:</span>
            <Tag color="blue">{record.dealer_name || "N/A"}</Tag>
          </div>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text: string) => (
        <span className="text-gray-500 italic">{text || "-"}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (record: any) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          Timeline
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Vehicle History & Dealer Activity
      </h1>

      <div className="space-y-6">
        {/* Search Bar */}
        <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm">
          <div className="flex-1 max-w-lg">
            <Search
              placeholder="Search by Dealer, Engine, Chassis, Type..."
              allowClear
              enterButton="Search"
              size="large"
              onSearch={handleSearch}
              onChange={(e) => {
                if (e.target.value === "") handleSearch("");
              }}
            />
          </div>
          <div className="text-gray-500 text-sm">
            Showing latest transfers and sales
          </div>
        </div>

        {/* Feed Table */}
        <Card className="shadow-sm border-none">
          <Table
            className="overflow-x-auto"
            dataSource={feedData?.historyData || []}
            columns={feedColumns}
            rowKey={(record: any) =>
              `${record.event_type}_${record.event_date}_${record.vehicle_id}`
            } // Unique key combo
            loading={isFetchingFeed}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: feedData?.total || 0,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
          />
        </Card>
      </div>

      {/* Detail Modal (Timeline View) */}
      <Drawer
        title="Detailed Vehicle History"
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        {isFetchingDetail ? (
          <div className="p-10 text-center">
            <Empty
              description="Loading Timeline..."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : detailData?.data && detailData.data.length > 0 ? (
          detailData.data.map((vehicle: any) => (
            <div key={vehicle.id} className="mb-4">
              <div className="flex justify-between items-start mb-6 border-b pb-4">
                <div>
                  <h2 className="text-lg font-bold text-blue-900">
                    {vehicle.v_type} - {vehicle.v_modal}
                  </h2>
                  <div className="text-gray-500 space-y-1 mt-1 text-sm">
                    <div>
                      Engine: <Tag>{vehicle.engine_number}</Tag>
                    </div>
                    <div>
                      Chassis: <Tag>{vehicle.chassis_number}</Tag>
                    </div>
                    <div>Color: {vehicle.color_name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <Tag
                    color={vehicle.status === "available" ? "blue" : "green"}
                  >
                    {vehicle.status.toUpperCase()}
                  </Tag>
                </div>
              </div>

              <h3 className="font-semibold mb-4 flex items-center gap-2 text-gray-700">
                <ClockCircleOutlined /> Full Timeline
              </h3>
              <Timeline mode="left">
                {vehicle.timeline?.map((event: any, index: number) => (
                  <Timeline.Item
                    key={index}
                    label={event.date}
                    color={getColor(event.type)}
                    dot={getIcon(event.type)}
                  >
                    <div className="font-medium text-gray-800">
                      {event.description}
                    </div>
                    {event.remarks && (
                      <div className="text-sm text-gray-500 mt-1 italic">
                        "{event.remarks}"
                      </div>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </div>
          ))
        ) : (
          <Empty description="No timeline data found." />
        )}
      </Drawer>
    </div>
  );
};
